import { createFileRoute, Link } from "@tanstack/react-router";
import { listBlogPosts } from "@/lib/blog/blog.functions";
import type { BlogSummary } from "@/lib/blog/blog.functions";
import { Logo } from "@/components/intel/Logo";

export const Route = createFileRoute("/blog/")({
  loader: () => listBlogPosts(),
  head: () => ({
    meta: [
      { title: "Intel Briefings — CARIO INTEL Blog" },
      { name: "description", content: "Daily briefings on OSINT tradecraft, threat intelligence, GEOINT, breach exposure, and Generative Engine Optimization. Published by the CARIO Intel Desk." },
      { property: "og:title", content: "CARIO INTEL — Intel Briefings" },
      { property: "og:description", content: "Daily OSINT, GEOINT, and threat-intel briefings from the CARIO Intel Desk." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://carionexus.com/blog" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://carionexus.com/blog" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "CARIO Intel Briefings",
        url: "https://carionexus.com/blog",
        publisher: { "@type": "Organization", name: "CARIO INTEL", url: "https://carionexus.com" },
      }),
    }],
  }),
  errorComponent: ({ error }) => (
    <div className="p-8 text-sm text-muted-foreground">Failed to load briefings: {error.message}</div>
  ),
  notFoundComponent: () => <div className="p-8">Not found</div>,
  component: BlogIndex,
});

function BlogIndex() {
  const posts = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-panel-border bg-background/70 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-6xl items-center px-4 sm:px-6">
          <Logo />
          <nav className="ml-auto flex items-center gap-4 text-sm">
            <Link to="/" className="text-muted-foreground hover:text-foreground">Home</Link>
            <Link to="/pricing" className="text-muted-foreground hover:text-foreground">Pricing</Link>
            <Link to="/login" className="text-muted-foreground hover:text-foreground">Sign in</Link>
          </nav>
        </div>
      </header>

      <section className="mx-auto max-w-4xl px-4 py-14 sm:py-20">
        <p className="font-mono text-xs text-cyan tracking-[0.3em]">INTEL_BRIEFINGS</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tight">
          Field notes from the CARIO Intel Desk
        </h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">
          Three briefings a day on OSINT tradecraft, GEOINT signals, breach exposure trends,
          threat intelligence, and Generative Engine Optimization.
        </p>

        <div className="mt-12 grid gap-4">
          {posts.length === 0 ? (
            <div className="glass rounded-2xl p-8 text-sm text-muted-foreground">
              First briefings publish shortly. Check back soon.
            </div>
          ) : (
            (posts as BlogSummary[]).map((p) => (
              <Link
                key={p.slug}
                to="/blog/$slug"
                params={{ slug: p.slug }}
                className="glass block rounded-2xl p-6 transition hover:border-cyan/50"
              >
                <div className="flex items-center gap-3 text-xs font-mono text-muted-foreground uppercase tracking-widest">
                  <span className="text-cyan">{p.category}</span>
                  <span>·</span>
                  <time dateTime={p.published_at}>
                    {new Date(p.published_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                  </time>
                  <span>·</span>
                  <span>{p.reading_minutes} min</span>
                </div>
                <h2 className="mt-3 font-display text-xl sm:text-2xl leading-tight">{p.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>
              </Link>
            ))
          )}
        </div>
      </section>
    </div>
  );
}